import type { IDisposable } from "@codesandbox/pitcher-client";
import { useEnvironmentInterface } from "environment-interface";
import { partition, sortBy } from "lodash";
import type { ThemeColorSchema } from "prism-react";
import { useTheme } from "prism-react";
import { useEffect } from "react";
import type { IColorTheme } from "vscode/services";
import { IThemeService, StandaloneServices } from "vscode/services";

type IColorThemeExtended = IColorTheme & {
  settingsId: string;
  extensionData: { extensionPublisher: string };
  baseTheme: string;
};
type IThemeServiceExtended = IThemeService & {
  getColorThemes: () => Promise<IColorThemeExtended[]>;
};

export const useColorSchemaCommand = () => {
  const { monacoEditor } = useEnvironmentInterface();
  const csbTheme = useTheme();

  const vsThemeService = StandaloneServices.get(
    IThemeService,
  ) as unknown as IThemeServiceExtended;

  const setVSCodeTheme = (theme: string) => {
    // Apply new theme to VS Code settings
    if (monacoEditor.libraryState.state === "LOADED") {
      monacoEditor.libraryState.monaco.editor.setTheme(theme);
    }
  };

  useEffect(() => {
    function setGlobalCssVariables(theme: IColorTheme) {
      const baseBackground = theme.getColor("editor.background", false);

      // Set this one specifically on the html because the element using this is rendered outside of the
      // editor
      document.documentElement.style.setProperty(
        "--vscode-header-border",
        theme.getColor("commandCenter.border", true)?.toString() ??
          "transparent",
      );
      document.documentElement.style.setProperty(
        "--preferred-bg-color",
        baseBackground?.toString() ?? "transparent",
      );
    }

    const theme = vsThemeService.getColorTheme();
    setGlobalCssVariables(theme);

    const disposable = vsThemeService.onDidColorThemeChange((color) => {
      const vscodeColor = color as IColorThemeExtended;
      const type = color.type.toLowerCase().includes("dark") ? "dark" : "light";

      const baseBackground = color.getColor("editor.background", false);
      const panelBackground = color.getColor("panel.background", true);
      setGlobalCssVariables(color);

      const data: ThemeColorSchema = {
        name: vscodeColor.settingsId,
        type,
        colors: {
          "neutral-bg-low": baseBackground?.darken(0.5).toString(),
          "neutral-bg-base": baseBackground?.toString(),
          "neutral-bg-subtle": baseBackground?.lighten(0.5).toString(),
          "neutral-bg-medium": baseBackground?.lighten(1).toString(),
          "neutral-bg-high": baseBackground?.lighten(1.5).toString(),

          "support-git-modified": color
            .getColor("gitDecoration.modifiedResourceForeground")
            ?.toString(),
          "support-git-deleted": color
            .getColor("gitDecoration.deletedResourceForeground")
            ?.toString(),
          "informative-success-base": color
            .getColor("gitDecoration.addedResourceForeground")
            ?.toString(),

          "support-system-selection": color
            .getColor("selection.background")
            ?.toString(),
        },
      };

      if (type === "light") {
        data.colors = {
          ...data.colors,
          "neutral-bg-low": baseBackground?.darken(0.08).toString(),
          "neutral-bg-base": color.getColor("background", false)?.toString(),
          "neutral-bg-subtle": panelBackground?.toString(),
          "neutral-bg-medium": panelBackground?.toString(),
          "neutral-bg-high": baseBackground?.darken(0.13).toString(),
        };
      }

      // Remove nullish values
      if (data.colors) {
        Object.keys(data.colors).forEach((key) => {
          // @ts-ignore
          if (data.colors[key] === undefined) {
            // @ts-ignore
            delete data.colors[key];
          }
        });
      }

      csbTheme.setTheme(data);
    });

    return disposable.dispose;
  }, []);

  const command = {
    id: "workbench.action.selectTheme",
    name: `Color Theme`,
    keywords: "themes colors",
    section: "Preferences",
    perform: async () => {
      const themes = await vsThemeService.getColorThemes();

      const VSCODE_THEMES_SUPPORTED = [
        "Dark (Visual Studio)",
        "Dark High Contrast",
        "Dark Modern",
        "Dark+",
        // "Light (Visual Studio)", // Disabled because the header doesn't fit
        "Light High Contrast",
        "Light Modern",
        // "Light+",  // Disabled because the header doesn't fit
        "Monokai",
      ];

      const supportedThemes = themes.filter((theme) => {
        return theme.extensionData.extensionPublisher === "vscode"
          ? VSCODE_THEMES_SUPPORTED.includes(theme.label)
          : true;
      });

      const api = await monacoEditor.getExtensionApi();
      interface Option {
        label: string;
        picked?: boolean;
        schema: IColorThemeExtended;
      }

      const [highContrast, regular] = partition(supportedThemes, (schema) => {
        return (
          schema.type === "hcDark" ||
          schema.type === "hcLight" ||
          schema.baseTheme === "hc-dark" ||
          schema.baseTheme === "hc-light"
        );
      });

      const [light, dark] = partition(regular, (schema) => {
        return schema.type === "light";
      });

      const [sortedLight, sortedDark, sortedHighContrast] = [
        light,
        dark,
        highContrast,
      ].map((schemas) => {
        return sortBy(schemas, (schema) => {
          return schema.label.toLocaleLowerCase();
        });
      });

      const mapOption = (schema: IColorThemeExtended): Option => {
        return {
          label: schema.settingsId,
          picked: schema.label === csbTheme.theme.name,
          schema,
        };
      };

      let themeChosen = false;
      const prevTheme = csbTheme.theme;
      const toDispose: IDisposable[] = [];
      const quickpick = api.window.createQuickPick();
      quickpick.placeholder = "Select Color Theme (Up/Down Keys to Preview)";
      quickpick.items = [
        { label: "light themes", kind: api.QuickPickItemKind.Separator },
        ...sortedLight.map(mapOption),
        { label: "dark themes", kind: api.QuickPickItemKind.Separator },
        ...sortedDark.map(mapOption),
        {
          label: "high contrast themes",
          kind: api.QuickPickItemKind.Separator,
        },
        ...sortedHighContrast.map(mapOption),
      ];
      quickpick.activeItems = quickpick.items.filter((item) => item.picked);
      toDispose.push(
        quickpick.onDidChangeActive((item) => {
          if (item.length === 0) {
            return;
          }
          const typedItem = item[0] as Option;

          setVSCodeTheme(typedItem.schema.settingsId);
        }),
      );
      toDispose.push(
        quickpick.onDidAccept(() => {
          themeChosen = true;

          quickpick.dispose();
          toDispose.forEach((disposable) => disposable.dispose());
        }),
      );
      toDispose.push(
        quickpick.onDidHide(() => {
          quickpick.dispose();
          toDispose.forEach((disposable) => disposable.dispose());

          if (!themeChosen) {
            setVSCodeTheme(prevTheme.name);
          }
        }),
      );

      quickpick.show();
    },
  };

  return command;
};
